import angular from 'angular';
import ExportWidgetLayoutModal from '@/vue/components/modals/ExportWidgetLayoutModal';
import ImportWidgetLayoutModal from '@/vue/components/modals/ImportWidgetLayoutModal';
import ConfirmDeletionModal from '@/vue/components/modals/ConfirmDeletionModal';
import _ from 'lodash';
import { EnumDefinitions } from "@/enums/custom-widget-axis-customisation";
import { REALTIME_INDICATOR } from '../../mqtt/mqtt.service';
import { handleFileDownload } from "@/utils/download";
import { scrollToWidgetById } from '@/utils/widgetScroll';

angular.module('vrm.core').controller('InstallationAdvancedController', InstallationAdvancedController);

/**
 * @constructor
 * @ngInject
 */
function InstallationAdvancedController(
    VrmModalService,
    $log,
    $scope,
    $window,
    $stateParams,
    $q,
    $timeout,
    $rootScope,
    InstallationsService,
    ApiService,
    GraphService,
    AdvancedGraphWidgetConfigs,
    AdvancedSummaryGraphWidgetConfigsGraphs,
    vrmNotificationsService,
    vrmNotificationType,
    locale,
    widgetTypeMapping,
    $filter,
    UserService,
    vrmUserAccessLevel,
    localStorageService,
    MQTTRealtimeService
) {
    const MOBILE_BREAKPOINT = 980; // todo check / move to constants or something

    $log.info('InstallationAdvancedController');

    $scope.hiddenQuickRanges = ['qr_last_12_months'];

    $scope.lockWidgets = false;
    $scope.lockedBeforeExpansion = false;
    $scope.gridFull = false;

    $scope.canCreateCustomWidgets = false;

    $scope.toggleLockWidgets = function () {
        localStorageService.set(storageKey(), !$scope.lockWidgets)
        $scope.lockWidgets = !$scope.lockWidgets;
    }

    $scope.recentWidgetSearches = [];

    $scope.$watch('installation.id', function () {
        if ($scope.installation && $scope.installation.id) {
            $scope.lockWidgets = localStorageService.get(storageKey()) || false;
            $scope.canCreateCustomWidgets = $scope.installation.view_permissions.update_settings || $rootScope.userState.isAdmin;

            getRecentWidgetSearches();
        }
    })

    function storageKey() {
        return [$scope.installation.id, "lockWidgets"].join('_');
    }

    $scope.customWidgets = {
        collapsed: false,
        widgets: []
    }

    $scope.realtimeIndicator = REALTIME_INDICATOR.OFFLINE;

    $scope.updateRealtimeIndicator = function (indicator) {
        // trick angular with a microtask to update the realtime indicator
        $timeout(() => {
            $scope.realtimeIndicator = indicator;
        })
    };

    $q.all([InstallationsService.getInstallation($stateParams.id), GraphService.ready()]).then(function (result) {
        var installation = result[0];
        if (InstallationsService.canMaintainRealtimeCommunication()) {
            MQTTRealtimeService.subscribe();
            MQTTRealtimeService.observeRealtime(installation.identifier, $scope.updateRealtimeIndicator);
        } else {
            $scope.realtimeIndicator = REALTIME_INDICATOR.OFFLINE;
        }
        // Pass the timezone offset to the graph service to display the installation time in the graphs.
        GraphService.highcharts.setOptions({
            time: installation.timezone ? {
                timezone: installation.timezone,
            } : {
                timezoneOffset: -installation.timezone_offset / 60,
            }
        });
    });

    $scope.attributeCodes = [];
    $scope.customWidgetsAttributeCodes = [];

    // Synchronized zoom state
    $scope.zoomstate = { min: null, max: null };

    $scope.panels = {
        search: false,
        filter: false,
    };

    // All available widgets for the current installation
    $scope.widgets = [];

    // All available widgets that match the current [widgetSearch.term] value.
    $scope.filteredWidgets = [];

    $scope.adminWidgets = [];
    $scope.originalWidgets = [];

    $scope.widgetSearch = {
        term: null
    };
    $scope.isSearchActive = false;

    // Widgets grouped by device/instance
    $scope.widgetGroups = [];

    $scope.isMobile = false;


    /**
     * Send the new widget layout to the API.
     *
     * @param newLayout
     */
    const persistWidgets = (newLayout = {}) => {
        if ($scope.isSearchActive || !$scope.installation.view_permissions.update_settings) {
            // Don't persist anything while the search bar is opened.
            return;
        }

        const widgets = [];
        const customWidgets = [];
        for (const index in $scope.widgets) {
            const widget = $scope.widgets[index];
            const layoutPosition = newLayout[widget.uniqueIdentifier];

            let position = {};
            if (layoutPosition) {
                const [x, y, w, h] = layoutPosition.split(',')
                position = {
                    row: +y,
                    col: +x,
                    sizeX: +w,
                    sizeY: +h
                };
                widget.row = position.row;
                widget.col = position.col;
                widget.sizeX = position.sizeX;
                widget.sizeY = position.sizeY;
            } else {
                position = {
                    row: +widget.row,
                    col: +widget.col,
                    sizeX: +widget.sizeX,
                    sizeY: +widget.sizeY
                };
            }

            // Don't persist the position of inactive widgets
            if (!widget.active) {
                delete widget.row
                delete widget.col
                delete widget.sizeX
                delete widget.sizeY
                position = {}
            }

            if (widget.idCustomWidget) {
                customWidgets.push({
                    idCustomWidget: widget.idCustomWidget,
                    instance: widget.instance,
                    active: widget.active,
                    ...position
                })
            } else if (widget.active) {
                widgets.push({
                    idWidget: widget.idWidget,
                    instance: widget.instance,
                    ...position
                });
            }
        }

        ApiService.installations($stateParams.id, 'settings', {
            widgets: widgets,
            customWidgets: customWidgets
        }, 'POST');
    }
    let persistWidgetsTimeout = null;
    $scope.delayedPersistWidgets = (...args) => {
        if (persistWidgetsTimeout) clearTimeout(persistWidgetsTimeout);
        persistWidgetsTimeout = setTimeout(() => persistWidgets(...args), 800);
    }

    $scope.disableWidget = (identifier) => {
        const widget = $scope.widgets.find((widget) => widget.uniqueIdentifier === identifier);
        if (widget) {
            widget.active = false;
            $scope.delayedPersistWidgets();
        }
    }

    $scope.registerSyncEventListeners = (container) => {
        GraphService.registerSyncEventListeners($(container));
    }

    $scope.widgetExpansionStart = () => {
        $scope.lockedBeforeExpansion = $scope.lockWidgets;
        $scope.lockWidgets = true;
    }

    $scope.widgetExpansionEnd = () => {
        if (!$scope.lockWidgets) {
            // User manually unlocked. Don't lock again even if it was locked before expansion.
            localStorageService.set(storageKey(), false)
        } else {
            $scope.lockWidgets = $scope.lockedBeforeExpansion;
        }
    }

    $scope.setGridFull = (gridFull) => {
        $scope.gridFull = gridFull;
    }

    // Get custom widgets in second array in response.data
    function fetchWidgets() {
        ApiService.installations($stateParams.id, 'active-widgets').then(function (response) {
            if (response.data.widgets && response.data.widgets instanceof Array) {
                $scope.widgets = response.data.widgets.map((widget) => {
                    widget.uniqueIdentifier = widget.instance + '_' + widget.idWidget;
                    widget.idle = ((new Date() / 1000) - widget.lastTimestamp) > 3600 * 24;
                    widget.isFullscreen = false;
                    return widget;
                });

                var unsortedWidgetGroups = _.groupBy($scope.widgets, function (widget) {
                    // treat device type 0 (gateway) as 6 (system overview)
                    var deviceType = parseInt(widget.deviceType) === 6 ? 0 : widget.deviceType;
                    return parseInt(deviceType) === 0 ? deviceType : deviceType + '_' + widget.instance;
                });

                // Sort widget groups by deviceType and instance
                // Place groups containing devices with ids0, 6 at the top.
                $scope.widgetGroups = _.orderBy(unsortedWidgetGroups, [
                    function (wg) { return +((wg[0].deviceType == 6 || wg[0].deviceType == 0) ? 0 : wg[0].deviceType); },
                    function (wg) { return +wg[0].instance; }
                ], ['asc', 'asc']);
            }
            $scope.customWidgets = {
                collapsed: false,
                widgets: []
            }
            addOrUpdateWidgets(response);

            // Map codes
            $scope.panels.filter = true;
            _.each($scope.widgets, function (widget) {
                if (typeof widgetTypeMapping[widget.code] != 'undefined')
                    widget.code = widgetTypeMapping[widget.code];

                if (widget.active) {
                    $scope.panels.filter = false;
                }
            });

            $scope.$watch('[widgets, customWidgets.widgets]', function () {
                var attributeCodes = {};
                var customWidgetsAttributeCodes = {};
                //Get all required attributes to be loaded for graph data in graph widgets
                for (var index in $scope.widgets) {
                    if ($scope.widgets[index].code !== 'CustomWidget' && $scope.widgets[index].active) {
                        var series, code;
                        // For simple graph widgets
                        for (series in AdvancedGraphWidgetConfigs[$scope.widgets[index].code]) {
                            for (code in AdvancedGraphWidgetConfigs[$scope.widgets[index].code][series].fields) {
                                if (!($scope.widgets[index].instance in attributeCodes)) {
                                    attributeCodes[$scope.widgets[index].instance] = [];
                                }
                                attributeCodes[$scope.widgets[index].instance].push(code);
                            }
                            const { extraFields } = AdvancedGraphWidgetConfigs[$scope.widgets[index].code][series]
                            if (extraFields) {
                                attributeCodes[$scope.widgets[index].instance] = _.union(attributeCodes[$scope.widgets[index].instance], extraFields);
                            }
                        }

                        // And then for summary-graph (mixed) widgets
                        for (series in AdvancedSummaryGraphWidgetConfigsGraphs[$scope.widgets[index].code]) {
                            for (code in AdvancedSummaryGraphWidgetConfigsGraphs[$scope.widgets[index].code][series].fields) {
                                if (!($scope.widgets[index].instance in attributeCodes)) {
                                    attributeCodes[$scope.widgets[index].instance] = [];
                                }
                                attributeCodes[$scope.widgets[index].instance].push(code);
                            }
                            const { extraFields } = AdvancedSummaryGraphWidgetConfigsGraphs[$scope.widgets[index].code][series];
                            if (extraFields) {
                                attributeCodes[$scope.widgets[index].instance] = _.union(attributeCodes[$scope.widgets[index].instance], extraFields);
                            }
                        }
                    }
                    if ($scope.widgets[index].code === 'CustomWidget' && $scope.widgets[index].active) {
                        customWidgetsAttributeCodes[$scope.widgets[index].idCustomWidget] = _.groupBy($scope.widgets[index].series, function (serie) {
                            return serie.code + '_' + serie.instance;
                        });
                        $scope.widgets[index].series = $scope.widgets[index].series.map((serie) => {
                            if (serie.customName) { serie.deviceName = serie.customName; }
                            return serie;
                        })
                    }
                }
                $scope.attributeCodes = attributeCodes;
                $scope.customWidgetsAttributeCodes = customWidgetsAttributeCodes;
                filterVisibleWidgets();
            }, true);
        });
    }

    /**
     * Watch for the search term to change and update the shown widgets accordingly.
     */
    $scope.$watchGroup(['panels.search', 'widgetSearch.term'], () => {
        $scope.isSearchActive = !!$scope.panels.search && !!$scope.widgetSearch.term;
        filterVisibleWidgets();
    }); 

    /**
     * Filter widgets based on active state, and clone to prevent changing the original positions
     * if necessary.
     */
    function filterVisibleWidgets() {
        if (!$scope.isSearchActive) {
            $scope.filteredWidgets = $scope.widgets.filter(w => w.active);
        } else {
            $scope.filteredWidgets = searchWidgets($scope.widgets, $scope.widgetSearch.term);
        }
        $scope.adminWidgets = $scope.filteredWidgets.filter(w => +w.minimumAccessLevel === 1);
        $scope.originalWidgets = $scope.filteredWidgets.filter(
            w => !w.minimumAccessLevel || +w.minimumAccessLevel === 0,
        );
    }

    /**
     * Filter the list of widgets for the installation based on the search term given by the user.
     *
     * @param allWidgets - List of all available/added widgets for this installation
     * @param searchTerm - The search term entered by the user.
     * @returns {*}
     */
    function searchWidgets(allWidgets, searchTerm) {
        const isEmptyTerm = !searchTerm || searchTerm.trim().length <= 0;
        $scope.debouncedSaveWidgetSearchTerm(searchTerm); // use the debounced function to avoid storing each keypress
        return allWidgets.reduce((filtered, w) => {
            const match = w.active && (isEmptyTerm || w.name.toLowerCase().indexOf(searchTerm.toLowerCase()) > -1);
            // We want a different uniqueIdentifier if we filter, as these widgets are fundamentally
            // not the same widgets as the unfiltered ones: their position and size in the grid is
            // completely disconnected.
            return match ? [...filtered, { ...w, uniqueIdentifier: `${w.uniqueIdentifier}_filtered` }] : filtered;
        }, []);
    }

    fetchWidgets();
    $scope.$on('refreshWidgets', fetchWidgets)

    if ($stateParams.widgetId) {
        scrollToWidgetById($stateParams.widgetId);
    }

    const updateLastSeenInterval = updateLastSeen(ApiService, $stateParams, $scope);

    /**
     * Fetches the recent widget searches for the current installation.
     *
     * This function makes an API call to retrieve the search history for widgets
     * associated with the current installation and updates the `$scope.recentWidgetSearches`
     * with the retrieved search history.
     *
     * @function getRecentWidgetSearches
     * @returns {void}
     */
    function getRecentWidgetSearches() {
        if (!$scope.installation || !$scope.installation.id) return;
        ApiService.getSiteSearchHistory($scope.installation.id, 'widget')
            .then(response => {
                if(response.success) {
                    $scope.recentWidgetSearches = response.search_history;
                }
            })
    }

    $scope.showLatestSearches = false;

    /**
     * Initializes event listeners for the widget search input field.
     *
     * Sets up event listeners to handle focus and click events
     * for the widget search input field. When the input and dropdown is focused, it
     * shows the latest searches. When a click occurs outside the input field or dropdown,
     * it hides the latest searches.
     *
     * The event listeners are stored in the `$scope` for later removal when
     * the component is destroyed.
     */
    $scope.initializeWidgetSearchListeners = function() {
        const widgetSearch = document.getElementById('widgetSearch');

        if (!widgetSearch) return;

        // Store event listeners so they can be removed later
        function handleFocusIn() {
            $scope.$apply(() => {
                $scope.showLatestSearches = true;
            });
        }

        function handleClickOutside(event) {
            if (!widgetSearch.contains(event.target)) {
                $scope.$apply(() => {
                    $scope.showLatestSearches = false;
                });
            }
        }

        // Attach event listeners
        widgetSearch.addEventListener('focusin', handleFocusIn);
        document.addEventListener('click', handleClickOutside);

        // Store the event listener references in $scope for removal later
        $scope.cleanupWidgetSearchListeners = function() {
            widgetSearch.removeEventListener('focusin', handleFocusIn);
            document.removeEventListener('click', handleClickOutside);
        };

    };

    // Use $timeout to ensure the DOM is ready before initializing the listeners
    $timeout(function() {
        $scope.initializeWidgetSearchListeners();
    }, 0);

    // Call cleanup function when the component is destroyed
    $scope.$on("$destroy", function() { if ($scope.cleanupWidgetSearchListeners) $scope.cleanupWidgetSearchListeners() });

    // Debounce function to prevent multiple API calls
    function debounce(fn, wait) {
        let timeout;
        return function(...args) {
            clearTimeout(timeout);
            timeout = setTimeout(() => {
                fn.apply(this, args);
            }, wait);
            this._debounceTimeout = timeout;
        };
    }


    /**
     * Saves the provided search term to the list of recent widget searches.
     *
     * Updates the list of recent widget searches by adding the new term
     * to the beginning of the list, ensuring there are no duplicates, and maintaining
     * a maximum of 5 recent searches. The updated list is then stored
     * and assigned to the `$scope.recentWidgetSearches`.
     *
     * @param {string} term - The search term to be saved.
     */
    function saveWidgetSearchTerm(term) {
        if (!term || term.trim().length === 0) return;

        const newSearchHistoryItem = {
            term: term,
            timestamp: Date.now()
        }

        let recentWidgetSearchesList = $scope.recentWidgetSearches;

        recentWidgetSearchesList = recentWidgetSearchesList.filter(searchEntry => searchEntry.term !== term);

        recentWidgetSearchesList.unshift(newSearchHistoryItem);


        if (recentWidgetSearchesList.length > 5) {
            recentWidgetSearchesList.pop();
        }

        ApiService.setSiteSearchHistory($scope.installation.id, 'widget', recentWidgetSearchesList);
        $scope.recentWidgetSearches = recentWidgetSearchesList;
    }

    // debounce this function to avoid storing each keypress
    $scope.debouncedSaveWidgetSearchTerm = debounce(saveWidgetSearchTerm, 1500);

    // Set the search term when a recent search is clicked
    $scope.selectRecentTerm = (term) => {
        $scope.widgetSearch.term = term;
    }

    // Remove a recent search term from the list
    $scope.removeRecentTerm = (term) => {
        let recentWidgetSearchesList = $scope.recentWidgetSearches;
        recentWidgetSearchesList = recentWidgetSearchesList.filter(searchEntry => searchEntry.term !== term);

        if ($scope.widgetSearch.term === term) {
            $scope.widgetSearch.term = '';
        }

        ApiService.setSiteSearchHistory($scope.installation.id, 'widget', recentWidgetSearchesList);
        $scope.recentWidgetSearches = recentWidgetSearchesList;
    }

    // trigger a highchart reload after installation updates to catch bad chart reflows
    $scope.$watch('installation', function () {
        setTimeout(() => $rootScope.$broadcast('highchartsng.reflow'), 2000);
    })

    function updateCustomWidgets(customWidgets) {
        if (customWidgets && customWidgets instanceof Array) {
            const oldWidgets = $scope.customWidgets.widgets.map(widget => ({
                uniqueIdentifier: widget.uniqueIdentifier,
                active: widget.active,
            }));
            $scope.customWidgets.widgets = customWidgets.map(customWidget => {
                customWidget.uniqueIdentifier = customWidget.idCustomWidget;
                customWidget.instance = customWidget.instance || 0;
                customWidget.idle =
                    new Date() / 1000 - customWidget.lastTimestamp > 3600 * 24;
                customWidget.canBeUpdated = canUpdateCustomWidget(customWidget);

                const instancesConfig = _.flatten(Object.values(customWidget.attributes).map(i => Object.values(i)));
                const attributeProperties = instancesConfig.reduce((rx, config) => {
                    const series = customWidget.series.find(
                        s =>
                            s.idDataAttribute === config.idDataAttribute &&
                            s.instance === config.instance
                    );
                    if (!series) {
                        console.error(
                            "Requested series was not found, contact developers about it",
                            { config, customWidget }
                        );
                        return rx;
                    }
                    const code = `${series.code}_${series.instance}`;
                    rx[code] = {
                        max: config.yAxisMax,
                        min: config.yAxisMin,
                        interval: config.yAxisInterval
                    };
                    return rx;
                }, {});

                customWidget.yAxisProperties = {
                    method: EnumDefinitions[customWidget.yAxisScaling],
                    globalMin: customWidget.yAxisMin,
                    globalMax: customWidget.yAxisMax,
                    globalInterval: customWidget.yAxisInterval,
                    attributeProperties
                };
                const oldWidget = oldWidgets.find(w => w.uniqueIdentifier === customWidget.uniqueIdentifier);
                customWidget.active = oldWidget ? oldWidget.active : customWidget.active; // keep active customWidgets (role: user)
                return customWidget;
            });
        }
    }

    $scope.realtime = false;

    $scope.datePicker = {
        range: {},
        control: {}
    };
    $scope.graphData = {};
    $scope.customWidgetGraphData = {};
    $scope.loading = false;
    $scope.updatingCustomWidgets = false;

    $scope.timeUserLeft = null;

    const pageVisibilityRootScope = $rootScope.$on('page-visibility-change', function(_, isVisible) {
        if (!isVisible) {
            $scope.timeUserLeft = new Date();
            clearInterval(updateLastSeenInterval);
        }
        if (isVisible) {
            if ($scope.timeUserLeft) {
                const timeUserReturned = new Date();
                const elapsedMinutes = (timeUserReturned - $scope.timeUserLeft) / (60 * 1000);
                if (elapsedMinutes >= 30) {
                    // force load the widget data
                    checkForChanges(true);
                }
                $scope.timeUserLeft = null;
            }
        }
    });

    $scope.$on('$destroy', function() {
        pageVisibilityRootScope()

        $timeout.cancel(dataLoadTimeout);
        // Fix memory leak in Angular Google Maps
        $scope.graphData = {};
        $scope.customWidgetGraphData = {};
        $scope.promises = [];
        MQTTRealtimeService.unsubscribe();
        clearInterval(updateLastSeenInterval);
        angular.element(window).off('resize');
    });

    $scope.exportLayout = () => {
        VrmModalService.openVueModal(ExportWidgetLayoutModal, { idSite: $scope.installation.id }, { fixedContentPos: true });
    };
    $scope.importLayout = () => {
        VrmModalService.openVueModal(ImportWidgetLayoutModal, { idSite: $scope.installation.id }, { fixedContentPos: true });
    };

    $rootScope.$on('deleteCustomWidget', function (event, customWidget) {
        $scope.deleteCustomWidget(customWidget);
    })

    $scope.deleteCustomWidget = (widget) => {
        VrmModalService.openDeleteModal($scope, 'custom_widget', widget.name, (confirmed) => {
            if (confirmed) {
                ApiService.installations($stateParams.id, `custom-widget`, { idCustomWidget: widget.idCustomWidget }, 'DELETE').then(function (response) {
                    if (response.data && response.data.success) {
                        $scope.customWidgets.widgets = $scope.customWidgets.widgets.filter(w => w.idCustomWidget !== widget.idCustomWidget);
                        $scope.widgets = $scope.widgets.filter(w => !w.idCustomWidget || w.idCustomWidget !== widget.idCustomWidget);
                        locale.ready('advanced').then(function () {
                            vrmNotificationsService.add({ type: vrmNotificationType.SUCCESS, message: locale.getString('overview.custom_widget_deleted') });
                        })
                    } else {
                        vrmNotificationsService.add({ type: vrmNotificationType.ERROR, message: response.data.errors }, true);
                    }
                });
            }
        });
    }

    /**
     * Load data and add to scope
     * @param instance id
     * @param url to download data from
     *
     * It adds the url to a loading urls array, and checks if it is already loading that url, in that case it aborts
     * the loading to prevent double requests
     */
    var loadingUrls = [];
    function loadData(instance, url) {
        if (loadingUrls.indexOf(url) > -1) return;
        loadingUrls.push(url);
        return ApiService.installations($scope.installation.id, url).then(function (response) {
            _removeLoadingUrl(url);
            return { response, instance };
        }, function () {
            _removeLoadingUrl(url);
        });
    }

    /**
     * Load custom widget data and add to scope
     * @param idCustomWidget id of custom widget to load
     * @param url to download data from
     *
     * It adds the url to a loading urls array, and checks if it is already loading that url, in that case it aborts
     * the loading to prevent double requests
     */
    function loadCustomWidgetData(idCustomWidget, url) {
        if (loadingUrls.indexOf(url) > -1) return;
        loadingUrls.push(url);
        return ApiService.installations($scope.installation.id, url).then(function (response) {
            _removeLoadingUrl(url);
            return { response, idCustomWidget };
        }, function () {
            _removeLoadingUrl(url);
        });
    }

    function storeCustomWidgetGraphData(resp) {
        var response = resp.response;
        var idCustomWidget = resp.idCustomWidget;
        var dataKey = Object.keys(response.data.records.data)[0];

        if (!$scope.customWidgetGraphData[idCustomWidget]) {
            $scope.customWidgetGraphData[idCustomWidget] = {
                data: {},
                meta: {}
            };
        }
        $scope.customWidgetGraphData[idCustomWidget].data[dataKey + '_' + response.data.records.instance] = response.data.records.data[dataKey];
        $scope.customWidgetGraphData[idCustomWidget].meta[dataKey + '_' + response.data.records.instance] = { ...response.data.records.meta[dataKey], code: response.data.records.meta[dataKey].code + '_' + response.data.records.instance };
    }

    // Remove url from loading queue
    function _removeLoadingUrl(url) {
        var index = loadingUrls.indexOf(url);
        if (index > -1)
            loadingUrls.splice(index, 1);
    }

    // Reset the zoom state of the graph changes.
    $scope.$watchGroup(['datePicker.range.startDate', 'datePicker.range.endDate'], () => {
        $scope.zoomstate = { min: null, max: null };
    });

    $scope.promises = [];
    //Reload data on change
    var loaded = false;
    var lastLoadedHash = {};
    $scope.$watchGroup(['datePicker.range.startDate', 'datePicker.range.endDate', 'attributeCodes', 'customWidgetsAttributeCodes', 'zoomstate.min', 'zoomstate.max'], checkForChanges);

    function checkForChanges(alwaysLoad=false) {
        // Load graph data from graph api
        if ($scope.attributeCodes && $scope.customWidgetsAttributeCodes && $scope.installation && $scope.datePicker.range.startDate) {
            // Only load data if tab is active in browser
            if ((typeof document.hidden != 'undefined' && document.hidden) && loaded) return;

            // Schedule load, only if last load was different
            var currentHash = {
                start: $scope.zoomstate.min ? Math.floor($scope.zoomstate.min / 1000) : $scope.datePicker.range.startDate,
                end: $scope.zoomstate.max ? Math.floor($scope.zoomstate.max / 1000) : $scope.datePicker.range.endDate,
                keepRealtimeStamp: !$scope.datePicker.range.endsNow && $scope.datePicker.range.keepRealtime ? Math.floor(+new Date() / 1000) : false,
                attributes: $scope.attributeCodes,
                customWidgetsAttributes: $scope.customWidgetsAttributeCodes,
            };

            if (!angular.equals(lastLoadedHash, currentHash) || alwaysLoad) {
                lastLoadedHash = currentHash;
                _scheduleDataLoad(currentHash.start, currentHash.end);
            }
        }
    }

    // Only do a data load every 300 ms, to prevent multiple loads
    var dataLoadTimeout = null;
    function _scheduleDataLoad(start, end) {
        $timeout.cancel(dataLoadTimeout);
        dataLoadTimeout = $timeout(function () {
            // Store timeoutID
            const timeoutId = dataLoadTimeout.$$timeoutId;
            // Set loading flag
            $scope.loading = true;
            $scope.$evalAsync();
            $scope.promises = [];
            for (var instance in $scope.attributeCodes) {
                var url = 'widgets/Graph' + "?start=" + start + "&end=" + end + "&instance=" + instance;
                const width = Math.floor(window.innerWidth < MOBILE_BREAKPOINT ? MOBILE_BREAKPOINT / 4 : MOBILE_BREAKPOINT / 3);
                url += '&width=' + width;
                url += '&useMinMax=1';
                url += '&pointsPerPixel=' + 1;
                for (var i in $scope.attributeCodes[instance]) {
                    // Skip total
                    if ($scope.attributeCodes[instance][i] != 'TOTAL') {
                        url += '&attributeCodes[]=' + $scope.attributeCodes[instance][i];
                    }
                }
                $scope.promises.push(loadData(instance, url));
            }

            for (var idCustomWidget in $scope.customWidgetsAttributeCodes) {
                Object.values($scope.customWidgetsAttributeCodes[idCustomWidget]).forEach(group => {
                    var url = 'widgets/Graph' + "?start=" + start + "&end=" + end + "&instance=" + group[0].instance;
                    const width = Math.floor(window.innerWidth < MOBILE_BREAKPOINT ? MOBILE_BREAKPOINT / 4 : MOBILE_BREAKPOINT / 3);
                    url += '&width=' + width;
                    url += '&useMinMax=1';
                    url += '&pointsPerPixel=' + 1;
                    group.forEach(attribute => {
                        url += '&attributeCodes[]=' + attribute.code;
                    });
                    // this has nothing to do with the get request, but rather to differentiate between custom widgets that
                    // have the same url due to the same data attributes selected
                    url += '&idCustomWidget=' + idCustomWidget;
                    $scope.promises.push(loadCustomWidgetData(idCustomWidget, url));
                });
            }

            $q.all($scope.promises).then(function (responses) {
                // A new data load has already started, discard this one.
                if (timeoutId !== dataLoadTimeout.$$timeoutId) return;

                //We process the graph data before passing it to the graphData object
                $scope.tempGraphData = {};
                responses.forEach((resp) => {
                    if (resp) {
                        if (resp.idCustomWidget) {
                            storeCustomWidgetGraphData(resp);
                        } else {
                            if ($scope.tempGraphData[resp.instance] === undefined) {
                                $scope.tempGraphData[resp.instance] = resp.response.data.records;
                            } else {
                                $scope.tempGraphData[resp.instance] = _.merge($scope.tempGraphData[resp.instance], resp.response.data.records);
                            }
                        }
                    }
                })
                $scope.graphData = {...$scope.tempGraphData};
                loaded = true;
                $scope.loading = false;
            });
        }, 300);
    }

    /**
     * The installation is periodically reloaded in the base.controller.js.
     * By watching it certain (realtime) actions can be perfomed.
     */
    $scope.$watch('installation', function () {
        // Update range to now only when the range is less than 24h, ends now, and the chart is not zoomed in
        if (
            $scope.datePicker.range.endDate -
                $scope.datePicker.range.startDate >
                86400 ||
            (!$scope.datePicker.range.endsNow && !$scope.datePicker.range.keepRealtime) ||
            $scope.zoomstate.min ||
            $scope.zoomstate.max
        )
            return;
        // Set end date to now which updates the pickers and trigger a checkForChanges() via watchers
        $scope.datePicker.control.refreshRange();
        if ($scope.datePicker.range.keepRealtime && !$scope.datePicker.range.endsNow) {
            checkForChanges();
        }
    });

    /**
     * Download data of installation in specified format
     * @param format for download (xlsx, csv, html)
     * @param datatype for download (log, kwh) or undefined for default
     * @param debug include debug or undefined for default
     */
    var downloadPending = false;
    $scope.download = function (format, datatype, debug) {
        // Check if we are already downloading data
        if (downloadPending) {
            locale.ready('advanced').then(function () {
                vrmNotificationsService.add({ type: vrmNotificationType.ERROR, message: locale.getString('advanced.download_pending') });
            });
            return;
        }

        // use async exports and mail the results if the export requested is for a duration longer than 4 hours
        var asyncDownload = $scope.datePicker.range.endDate - $scope.datePicker.range.startDate > 60 * 60 * 4;
        var payload = {
            format: format,
            datatype: datatype,
            debug: debug,
            start: $scope.datePicker.range.startDate,
            end: $scope.datePicker.range.endDate,
            async: asyncDownload,
        }

        VrmModalService.openDownloadModal($scope, 'advanced.preparing_download', false);
        downloadPending = true;

        if (asyncDownload) {
            ApiService.call('/installations/' + $stateParams.id + '/data-download', payload, 'GET', false, 'json', false).then(() => {
                $timeout(function () {
                    $.magnificPopup.close();
                }, 510);
                $timeout(function () {
                    UserService.getUser()
                        .then(function (user) {
                            return user.email;
                        })
                        .catch(() => '<error>')
                        .then(email => {
                            VrmModalService.openDownloadModal($scope, 'advanced.preparing_download_async', true, { email });
                        });
                }, 1020);
                downloadPending = false;
            }, () => {
                downloadPending = false;
            });
        } else {
            ApiService.installationDownload($scope.installation.id, 'data-download', payload, false).then(function (response) {
                var siteDescription = $scope.installation && $scope.installation.name ? $scope.installation.name.toString().replace(/[^a-zA-Z0-9]+/g, "") : "";
                var dateFormat = 'yyyyMMdd-HHmm';
                var start = $filter('date')($scope.datePicker.range.startDate * 1000, dateFormat, $scope.installation.timezone);
                var end = $filter('date')($scope.datePicker.range.endDate * 1000, dateFormat, $scope.installation.timezone);
                // Detect cases where xlsx is exported when xls cannot support the column count
                if (response.headers()['content-type'] === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
                    format = "xlsx";
                }

                handleFileDownload(
                    response.data,
                    siteDescription + "_" + start + "_to_" + end + (datatype ? "_" + datatype : "") + "." + format
                );
                downloadPending = false;
                $.magnificPopup.close();
            }, function () {
                downloadPending = false;
                $.magnificPopup.close();
            });
        }
    };

    $scope.noWidgetsText = function () {
        return $scope.installation && $scope.installation.view_permissions.update_settings ? 'advanced.no_widgets' : 'advanced.no_widgets_readonly';
    }

    $scope.$on('hideWidgetRequest', (event, widgetToHide) => {
        event.stopPropagation();
        const widget = $scope.widgets.find(widget =>
            (widgetToHide.isCustomWidget && widget.idCustomWidget === widgetToHide.id) ||
            (widget.idWidget === widgetToHide.id && widget.instance === widgetToHide.instance)
        );
        if (widget) {
            widget.active = false;
        }
    });

    $scope.getMaxLastTimestamp = function (widgetGroup) {
        var maxTimestamp = 0;
        for (var i = 0; i < widgetGroup.length; i++) {
            if (widgetGroup[i].lastTimestamp > maxTimestamp) {
                maxTimestamp = widgetGroup[i].lastTimestamp;
            }
        }
        return maxTimestamp * 1000; // convert to milliseconds for amTimeAgo filter
    };

    $rootScope.$emit('checkMapsAvailabilityEvent');


    $scope.toggleWidgets = function (widgetCategory = "all", action = true, deviceType = null, instance = null, isCustomWidget = false) {
        $scope.widgets.filter(function (widget) {
            if (widgetCategory == "all") return true;
            if (isCustomWidget) return widget.idCustomWidget;
            if((deviceType == "0"  || deviceType == "6") && (widget.deviceType == "0" || widget.deviceType == "6")){
                // treat device type 0 (gateway) as 6 (system overview)
                return widget.instance == instance && !widget.idCustomWidget
            }
            return widget.deviceType == deviceType && widget.instance == instance && !widget.idCustomWidget;
        }).forEach(function (widget) {
            widget.active = action;
        });

        locale.ready('advanced').then(function () {
            const notificationContent = locale.getString(`advanced.${action ? "enable" : "disable"}_${widgetCategory == "all" ? "all" : "type"}_widgets_confirm`);
            vrmNotificationsService.add({ type: vrmNotificationType.SUCCESS, message: notificationContent });
        })
        $scope.delayedPersistWidgets();
    }

    $scope.openAiAssistant = function () {
        window.dispatchEvent(new CustomEvent('open-ai-assistant-view'));
    }

    $scope.countActiveWidgets = function (widgetCategory = "all", deviceType = null, instance = null, isCustomWidget = false) {
        return $scope.widgets.filter(function (widget) {
            if (widgetCategory == "all") return widget.active;
            if (isCustomWidget) return widget.active && widget.idCustomWidget
            if((deviceType == "0"  || deviceType == "6") && (widget.deviceType == "0" || widget.deviceType == "6")){
                // treat device type 0 (gateway) as 6 (system overview)
                return widget.active && widget.instance == instance && !widget.idCustomWidget
            }
            return widget.active && widget.deviceType == deviceType && widget.instance == instance && !widget.idCustomWidget
        }).length;
    };

    function updateLastSeen(ApiService, $stateParams) {
        return setInterval(function () {
            ApiService.installations($stateParams.id, 'active-widgets').then(function(response) {
                addOrUpdateWidgets(response);
                $scope.searchWidgets = angular.copy($scope.widgets);

                // Map codes
                _.each($scope.widgets, function(widget) {
                    if (typeof widgetTypeMapping[widget.code] != 'undefined')
                        widget.code = widgetTypeMapping[widget.code];

                });
            });
        }, 1000 * 60 * 5); // 5 minutes
    }


    function addOrUpdateWidgets(response) {
        if (response.data.widgets && response.data.widgets instanceof Array) {
            const oldWidgets = $scope.widgets.map(widget => ({
                uniqueIdentifier: widget.uniqueIdentifier,
                active: widget.active,
            }));

            $scope.widgets = response.data.widgets.map((widget) => {
                widget.uniqueIdentifier = widget.instance + '_' + widget.idWidget;
                widget.idle = ((new Date() / 1000) - widget.lastTimestamp) > 3600 * 24;
                widget.isFullscreen = false;
                widget.x = widget.col;
                widget.y = widget.row;
                widget.rows = widget.sizeY;
                widget.cols = widget.sizeX;
                const oldWidget = oldWidgets.find(w => w.uniqueIdentifier === widget.uniqueIdentifier);
                widget.active = oldWidget ? oldWidget.active : widget.active; // keep active widgets (role: user)
                return widget;
            });

            let unsortedWidgetGroups = _.groupBy($scope.widgets, function (widget) {
                // treat device type 0 (gateway) as 6 (system overview)
                var deviceType = parseInt(widget.deviceType) === 6 ? 0 : widget.deviceType;
                return parseInt(deviceType) === 0 ? deviceType : deviceType + '_' + widget.instance;
            });

            // Sort widget groups by deviceType and instance
            // Place groups containing devices with ids0, 6 at the top.
            let sortedWidgetGroups = _.orderBy(unsortedWidgetGroups, [
                    function (wg) { return +((wg[0].deviceType == 6 || wg[0].deviceType == 0) ? 0 : wg[0].deviceType); },
                    function (wg) { return +wg[0].instance; }
                ], ['asc', 'asc']);

            if ($scope.widgetGroups && $scope.widgetGroups.length > 0) {
                sortedWidgetGroups.forEach((widgetGroup) => {
                    // keep widgetGroup.collapsed state from the existing widgetGroups
                    $scope.widgetGroups.forEach((existingWidgetGroup) => {
                        if (existingWidgetGroup[0].deviceType === widgetGroup[0].deviceType && existingWidgetGroup[0].instance === widgetGroup[0].instance) {
                            widgetGroup.collapsed = existingWidgetGroup.collapsed;
                        }
                    });
                });
            }
            $scope.widgetGroups = sortedWidgetGroups;
            updateCustomWidgets(response.data.customWidgets);
            $scope.widgets = $scope.widgets.concat($scope.customWidgets.widgets);
            // Remove position data from inactive widgets so on activation they get the first available
            // position not the existing one which could overlap and seem random
            $scope.widgets = $scope.widgets.map(w => {
                if (!w.active) {
                    delete w.row
                    delete w.col
                    delete w.sizeX
                    delete w.sizeY
                }
                return w;
            });
        }
    }

    function canUpdateCustomWidget(widget) {
        if ($scope.installation && $scope.installation.view_permissions && $scope.installation.view_permissions.update_settings) {
            return true;
        }
        if (!$rootScope.userState || $rootScope.userState.isShare) {
            return false;
        }
        if ($rootScope.user.accessLevel === vrmUserAccessLevel.ADMIN) {
            return true;
        }
        if ($rootScope.user.accessLevel === vrmUserAccessLevel.RESTRICTED_ADMIN) {
            return +widget.minimumAccessLevel > 0;
        }
        return false;
    }
}
